
import { makeStyles } from "@material-ui/core";
import { getSentimentText, isDeepOTM, optionColorShouldBeGreen, optionColorShouldBeGreenOnlyType, formatMoney, getBullflowColorPrefCB, getBullflowTimezonePref, analyticsLogError, isAfterMarketHours, isMarketHoursEastern, optionColorForAlert } from "./Utils";
import { Tooltip } from '@material-ui/core';
import { LockClosedIcon } from "@radix-ui/react-icons";
import { useEffect, useState, useRef } from "react";
import debounce from 'lodash/debounce';
const bullflowColorPref = getBullflowColorPrefCB();
const bullflowLocalTimezonePref = getBullflowTimezonePref();
const BULLFLOW_GREEN = "#20d46b";
const BULLFLOW_RED = "#cc3753";
const BULLFLOW_OPACITY_GREEN = "rgba(2, 212, 44, 0.15)";
const BULLFLOW_OPACITY_RED = "rgba(250, 0, 67, 0.15)";
const CB_OPACITY_GREEN = "rgba(44, 140, 90, 0.15)";
const CB_OPACITY_RED = "rgba(211, 84, 0, 0.15)";
const CB_GREEN = "#2c8c5a";
const CB_RED = "#d35400";
const GREEN_ = bullflowColorPref ? CB_GREEN : BULLFLOW_GREEN;
const RED_ = bullflowColorPref ? CB_RED : BULLFLOW_RED;
const OPACITY_GREEN_ = bullflowColorPref ? CB_OPACITY_GREEN : BULLFLOW_OPACITY_GREEN;
const OPACITY_RED_ = bullflowColorPref ? CB_OPACITY_RED : BULLFLOW_OPACITY_RED;

const tooltips = {
    SWEEP: "Executed rapidly across multiple markets simultaneously to take advantage of the best available prices.",
    BLOCK: "Executed on single exchange. Larger trades may be negotiated off the exchange.",
    SPLIT: "Executed across two markets to achieve better price realization and liquidity.",
    MULTI: "A multileg trade involving the buying or selling of multiple options in a single transaction.",
};

const useStyles = makeStyles({
    dataTypeWhite: {
        color: 'white'
    },
    dataTypeBlue: {
        color: '#ffa42e '
    },
    logo: {
        height: '15px',
        margin: 0,
        padding: 0,
        marginRight: '10px',
        cursor: 'pointer',
    },
    dataTypeGreen: {
        color: GREEN_,
        fontWeight: 500,
    },
    dataTypeRed: {
        color: RED_,
        fontWeight: 500,
    },
});

const GuestLockCellRenderer = (params) => {
    // Just show a lock icon
    return (
        <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
            <Tooltip enterDelay={1000} enterNextDelay={500} title="Subscribe to unlock this data">
                <LockClosedIcon />
            </Tooltip>
        </div>
    );
};


const TickerCellRenderer = (params) => {
    const value = params?.value;
    const ot = params?.data?.ot;
    const optionColorShouldBeGreen_ = optionColorShouldBeGreenOnlyType(ot);
    const greenType = bullflowColorPref ? CB_GREEN : BULLFLOW_GREEN;
    const redType = bullflowColorPref ? CB_RED : BULLFLOW_RED;
    const OPACITY_GREEN_ = bullflowColorPref ? CB_OPACITY_GREEN : BULLFLOW_OPACITY_GREEN;
    const OPACITY_RED_ = bullflowColorPref ? CB_OPACITY_RED : BULLFLOW_OPACITY_RED;
    const wrapperStyle = {
        borderRadius: '5px',
        color: '#fff',
        backgroundColor: optionColorShouldBeGreen_ ? OPACITY_GREEN_ : OPACITY_RED_,
        borderColor: optionColorShouldBeGreen_ ? greenType : redType,
        borderWidth: '1px',
        borderStyle: 'solid',
        display: 'inline-block',
        padding: '5px 5px',
        lineHeight: '15px',
        width: '60px',
        textAlign: 'center',
    };

    return (
        <div style={wrapperStyle}>{value}</div>
    );
};

const OptionTypeCellRenderer = (params) => {
    const value = params?.value;
    const ot = params?.data?.ot;
    const classes = useStyles();
    return (
        <div className={`${optionColorShouldBeGreenOnlyType(ot) ? classes.dataTypeGreen : classes.dataTypeRed} ${classes.cell}`}>
            {value === "P" ? "Put" : "Call"}
        </div>
    );
}


const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}
const TradeTypeCellRenderer = (params) => {
    let value = params?.value;
    if (params?.data?.im) {
        value = "MULTI";
    }
    const style = {
        color: value === "SWEEP" ? '#e3c102' :
            value === "BLOCK" ? '#027df7' :
                value === "SPLIT" ? '#b223fa' : 'white'
    };
    const displayValue = capitalizeFirstLetter(value);

    const tooltip = tooltips[value.toUpperCase()] || "Standard trade type";
    return (
        <Tooltip enterDelay={1000} enterNextDelay={500} title={tooltip}>
            <div style={style}>
                {displayValue}
            </div>
        </Tooltip>
    );
}

const OpenInterestCellRenderer = (params) => {
    let value = params?.value;
    return (
        <div >
            {value == null ? "N/A" : value}
        </div>
    );
}

const TickerSentimentCellRenderer = (params) => {
    const value = params?.value;
    const classes = useStyles();
    return (
        <div className={value === 1 ? classes.dataTypeGreen :
            value === -1 ? classes.dataTypeRed :
                classes.dataTypeWhite}>
            {getSentimentText(value)}
        </div>
    )
}

const TradePriceCellRenderer = (params) => {
    const value = params?.value;
    return (
        <div>
            {value}
        </div>
    );
}

const TradeSizeCellRenderer = (params) => {
    const size = params?.value;
    const oi = params?.data?.oi;
    const volume = params?.data?.cv;
    if (oi != null && size > oi) {
        // return text that is #29ab8a greenish
        return (
            <Tooltip enterDelay={1000} enterNextDelay={500} title="Trade size is greater than open interest. Unusual.">
                <div style={{ color: '#19cfa0' }}>
                    {size}
                </div>
            </Tooltip>
        );
    } else if (volume != null && oi != null && volume > oi) {
        //return text that is #ab299c purpleish
        return (
            <Tooltip enterDelay={1000} enterNextDelay={500} title="Contract volume is greater than open interest.">
                <div style={{ color: '#d117bb' }}>
                    {size}
                </div>
            </Tooltip>
        );
    } else {
        return (
            <div>
                {size}
            </div>
        );
    }
}


const UnixTimestampCellRenderer = (params) => {
    const value = params?.value;

    // Configure date options based on timezone preference
    const dateOptions = {
        hour12: false,
        timeZone: bullflowLocalTimezonePref ? undefined : 'America/New_York' // undefined uses local timezone
    };

    // Tooltip content changes based on timezone preference
    const tooltipContent = bullflowLocalTimezonePref
        ? "Time of trade, local timezone"
        : "Time of trade, EST Time zone";

    return (
        <Tooltip enterDelay={1000} enterNextDelay={500} title={tooltipContent}>
            <div style={{ color: '#858585' }}>
                {new Date(value * 1000).toLocaleTimeString('en-US', dateOptions)}
            </div>
        </Tooltip>
    );
};

const HistoricalFlowDateTimeCellRenderer = (params) => {
    const value = params?.value;
    const tooltipContent = bullflowLocalTimezonePref
        ? "Time of trade, local timezone"
        : "Time of trade, EST Time zone";

    return (
        <Tooltip enterDelay={1000} enterNextDelay={500} title={tooltipContent}>
            <div>
                {new Date(value * 1000).toLocaleString('en-US', {
                    timeZone: bullflowLocalTimezonePref ? undefined : 'America/New_York',
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false,
                })}
            </div>
        </Tooltip>
    );
};
const OTMCellRenderer = (params) => {
    const value = params?.value;
    return (
        <div>
            {value}
        </div>
    );
}

const TrendingContractPremiumCellRenderer = (params) => {
    const value = params?.value;
    const classes = useStyles();
    return (
        <div className={`${classes.dataTypeWhite}  ${classes.cell}`}>
            {'$' + formatMoney(value)}
        </div>
    );
}

const PremiumCellRenderer = (params) => {
    const value = params?.value;
    const classes = useStyles();
    return (
        <div className={`${classes.dataTypeWhite}  ${classes.cell}`}>
            {'$' + formatMoney(value)}
        </div>
    );
}

const EndOfDayPerformanceCellRenderer = (params) => {
    const value = params?.value;
    const classes = useStyles();
    // console.log("EndOfDayPerformanceCellRenderer", value);
    if (value === null || value === undefined) {
        return (
            <Tooltip enterDelay={1000} enterNextDelay={500} title="The highest % return this trade experienced after it was executed, within
            the day.">
                <div className={`${classes.dataTypeWhite}  ${classes.cell}`}>
                    N/A
                </div>
            </Tooltip>
        )
    }
    return (
        <Tooltip enterDelay={1000} enterNextDelay={500} title="The highest % return this trade experienced after it was executed, within the day. Wide spread
        between bid and ask prices may cause this to be inaccurate.">
            <div className={`${value !== undefined ? (value > 0 ? classes.dataTypeGreen : classes.dataTypeRed) : classes.dataTypeWhite}  ${classes.cell}`}>
                {value !== undefined ? (value >= 0 ? '+' + value + '%' : value + '%') : 'N/A'}
            </div>
        </Tooltip>
    )
}

const SigScoreCellRenderer = (params) => {
    let value = params?.value;
    if (typeof value === 'number') {
        value = Number(value.toFixed(2));
    }



    if (!value) {
        return null;
    }
    let color;
    if (value <= 0.2) {
        color = '#fa0043';
    } else if (value > 0.2 && value <= 0.3) {
        color = '#f71707';
    } else if (value > 0.3 && value <= 0.4) {
        color = '#f74f07';
    } else if (value > 0.4 && value <= 0.5) {
        color = '#f76f07';
    } else if (value > 0.5 && value <= 0.6) {
        color = '#f79307';
    } else if (value > 0.6 && value <= 0.7) {
        color = '#f7cf07';
    } else if (value > 0.7 && value <= 0.8) {
        color = '#c3f707';
    } else if (value > 0.8 && value <= 0.89) {
        color = '#73f707';
    } else if (value > 0.89 && value <= 0.95) {
        color = '#24e00b'
    } else if (value > 0.95 && value <= 1.00) {
        color = '#25ff08'
    } else {
        color = '#FFD700';
    }

    let percentage = value * 100;

    return (
        <Tooltip enterDelay={1000} enterNextDelay={500} title="Indicates how unusual the trade is. The closer to 1.0, the more unusual.">
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
            }}>
                <div style={{
                    width: '80%',
                    height: '8px',
                    borderRadius: '10px',
                    background: '#15111f',
                    fontSize: '0.65em',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    <div style={{
                        width: `${percentage}%`,
                        height: '10px',
                        backgroundColor: color,
                        borderRadius: '10px',
                        marginRight: '2px'
                    }}>
                        {value}
                    </div>
                </div>
            </div>
        </Tooltip>
    );
}

const SpeadExecutionCellRenderer = (params) => {
    const classes = useStyles();
    const executionValue = params?.value;
    return (
        <Tooltip enterDelay={1000} enterNextDelay={500} title={
            executionValue === "Above Ask" ? "Executed Above Ask" :
                executionValue === "Below Bid" ? "Executed Below Bid" :
                    executionValue === "Bid" ? "Executed at/near bid" :
                        executionValue === "Ask" ? "Executed at/near ask" :
                            "Executed at/near Mark"
        }>
            <div className={classes.dataTypeWhite}>
                {executionValue === "Above Ask" ? "AA" :
                    executionValue === "Below Bid" ? "BB" :
                        executionValue === "Below Bid" ? "BB" :
                            executionValue === "Below Bid" ? "BB" :
                                executionValue}
            </div>
        </Tooltip>
    )
}

const ImpliedVolatilityCellRenderer = (params) => {
    const value = params?.value;
    const parsedValue = parseFloat(value);

    return (
        <div>
            {value == null || isNaN(parsedValue) ? "N/A" : parsedValue}
        </div>
    );
}


const CurrentVolumeCellRenderer = (params) => {
    const value = params?.value;
    return (
        <Tooltip enterDelay={1000} enterNextDelay={500} title="Total volume of the contract at the time of execution">
            <div>
                {value === "" || value === null ? "N/A" : value}
            </div>
        </Tooltip>
    );
}

const PreviousVolumeCellRenderer = (params) => {
    const value = params?.value;
    return (
        <Tooltip enterDelay={1000} enterNextDelay={500} title="Yesterday total volume">
            <div>
                {value === "" ? "N/A" : value}
            </div>
        </Tooltip>
    );
}


const DeltaCellRenderer = (params) => {
    const value = params?.data?.g?.d?.toFixed(3);
    return (
        <div>
            {value}
        </div>
    );
}

async function fetchPercentReturn(optionsTicker, oldSpread, forHighestReturn = false, mtd = null, ut = null) {
    try {
        let url = null;
        if (forHighestReturn) {
            url = `https://api.bullflow.io/v1/highestReturn?sym=${optionsTicker}&old_price=${oldSpread}&trade_ms_of_day=${mtd}&trade_timestamp=${ut}`;
        } else {
            url = `https://api.bullflow.io/v1/spreadPercentChange?Symbol=${optionsTicker}&spread=${oldSpread}`;
        }
        // console.log(`Fetch percent return: ${url}`);
        const response = await fetch(url);
        if (response.ok) {
            return await response.json();
        } else {
            console.error('There was an issue retrieving the options spread.');

            analyticsLogError('There was an issue retrieving the options spread.');
            return null;
        }
    } catch (error) {
        analyticsLogError(`option spread error: ${error}.`);
        console.error('Error:', error);
        return null;
    }
}


export const HighestReturnCellRenderer = (params) => {
    // 1️⃣ Wrapper: no hooks here, just the conditional
    if (isMarketHoursEastern()) {
        // show N/A
        return <p
            style={{
                color: '#858585',
                fontWeight: 550,
                opacity: 1,
                transition: 'opacity .5s ease-in'
            }}>N/A</p>;
    }
    return <HighestReturnCellRendererInner {...params} />;
};

const HighestReturnCellRendererInner = ({ data: row }) => {
    const { sym: optionsTicker, tp: oldSpread, mtd: tradeMsOfDay, ut: tradeTimestamp, eod } = row || {};
    const { data, loading } = useAsyncPerformance(
        optionsTicker, oldSpread, true, tradeMsOfDay, tradeTimestamp, eod
    );
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        let fadeInTimer;
        if (loading) {
            setVisible(false);
        } else {
            fadeInTimer = setTimeout(() => setVisible(true), 20);
        }
        return () => clearTimeout(fadeInTimer);
    }, [loading]);

    if (loading) return <p />;

    const getColor = v => (v >= 0 ? '#20d46b' : '#cc3753');
    const displayValue = (data == null) ? 'N/A' : `${data}%`;

    return (
        <Tooltip
            enterDelay={750}
            enterNextDelay={500}
            title="Peak % return of this trade based on the contract price high since trade execution."
        >
            <div
                style={{
                    color: data == null ? '#888' : getColor(data),
                    fontWeight: 550,
                    opacity: visible ? 1 : 0,
                    transition: 'opacity .5s ease-in'
                }}
            >
                {displayValue}
            </div>
        </Tooltip>
    );
};


export const LivePerformanceCellRenderer = (params) => {
    const { data: row } = params;
    const { sym: optionsTicker, tp: oldSpread } = row || {};

    const { data, loading } = useAsyncPerformance(optionsTicker, oldSpread);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        let fadeTimer;
        if (loading) {
            // hide immediately on new fetch
            setVisible(false);
        } else {
            // fade in on *any* finished state
            fadeTimer = setTimeout(() => setVisible(true), 20);
        }
        return () => clearTimeout(fadeTimer);
    }, [loading]);

    if (loading) {
        return <p />;
    }

    // color green for >=0, red for <0, gray for N/A
    const getColor = (value) =>
        value == null
            ? '#888'
            : value >= 0
                ? '#20d46b'
                : '#cc3753';

    // only null/undefined → "N/A"
    const displayValue = (data == null) ? 'N/A' : `${data}%`;

    return (
        <Tooltip
            enterDelay={1000}
            enterNextDelay={500}
            title="The current return of this trade based on latest contract price vs contract price at the time of trade."
        >
            <div
                style={{
                    color: getColor(data),
                    fontWeight: 550,
                    opacity: visible ? 1 : 0,
                    transition: 'opacity 0.5s ease-in',
                }}
            >
                {displayValue}
            </div>
        </Tooltip>
    );
};

export function useAsyncPerformance(
    optionsTicker,
    oldSpread,
    forHighestReturn,
    msTimeOfDay = null,
    timestamp = null,
    eod = null
) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // 1️⃣ Create a single debounced function on first render
    const debouncedFetch = useRef(
        debounce(async (ticker, spread) => {
            setLoading(true);
            try {
                const result = await fetchPercentReturn(
                    ticker,
                    spread,
                    forHighestReturn,
                    msTimeOfDay,
                    timestamp
                );
                if (result != null) {
                    // ensure eod is a number'
                    if (eod != null && !isNaN(eod)) {
                        if (result[0] < eod) {
                            setData(eod);
                        } else {
                            setData(result[0]);
                        }
                    } else {
                        // console.log("EOD is not a number");
                        setData(result[0]);
                    }
                } else {
                    // console.log("Highest return data is null");
                    setData(null);
                }
            } finally {
                setLoading(false);
            }
        }, 1000)    // <-- 300ms after the last call, you’ll fire exactly once
    );

    // 2️⃣ When your inputs change, call that same debounced function
    useEffect(() => {
        if (optionsTicker && oldSpread != null) {
            debouncedFetch.current(optionsTicker, oldSpread);
        }
    }, [optionsTicker, oldSpread]);

    // 3️⃣ Clean up on unmount so you don’t update state after cells disappear
    useEffect(() => {
        return () => {
            debouncedFetch.current.cancel();
        };
    }, []);

    return { data, loading };
}

export { DeltaCellRenderer, TradeSizeCellRenderer, TradeTypeCellRenderer, EndOfDayPerformanceCellRenderer, HistoricalFlowDateTimeCellRenderer, GuestLockCellRenderer, TrendingContractPremiumCellRenderer, ImpliedVolatilityCellRenderer, OpenInterestCellRenderer, CurrentVolumeCellRenderer, PreviousVolumeCellRenderer, SpeadExecutionCellRenderer, SigScoreCellRenderer, PremiumCellRenderer, OTMCellRenderer, UnixTimestampCellRenderer, TradePriceCellRenderer, TickerSentimentCellRenderer, TickerCellRenderer, OptionTypeCellRenderer };
